/** @file socket.set: points a named socket at a node, or removes it. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { NAME_PATTERN } from '@zilo/pose3d-format/validate/fields/namePattern';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { readInput } from '../validate/readInput';

const socketName = checkString(NAME_PATTERN);

/** `socket.set { socket, node }` → `{}`; `node: null` removes the socket; inverse: the previous node (or null). */
export const socketSetCommand: CommandDef<'socket.set'> = {
  name: 'socket.set',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['socket', 'node']);
    return { socket: r.check('socket', socketName) as string, node: r.nullable('node', r.str) };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const p = draft.puppet;
    if (input.node !== null && !Object.hasOwn(p.nodes, input.node)) {
      throwError(ERROR_CODES.NOT_FOUND, { node: input.node }, { path: '/input/node', hint: 'a node of the puppet' });
    }
    const inverse = [{ name: 'socket.set' as const, input: { socket: input.socket, node: p.sockets[input.socket] ?? null } }];
    if (input.node === null) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- sockets are keyed by name
      delete p.sockets[input.socket];
    } else p.sockets[input.socket] = input.node;
    return { result: {}, warnings: [], inverse };
  },
};
